import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type { AdminUserListRow } from '@/types/admin';

const ADMIN_SEARCH_DEBOUNCE_MS = 300;
const ADMIN_SEARCH_LIMIT = 50;

/**
 * Debounced super-admin user list (with optional search). Wraps the
 * staff-gated `admin_list_users(p_q, p_limit)` RPC: empty query returns
 * the most-recently-created users; non-empty query filters across email,
 * first_name, last_name with case-insensitive ILIKE.
 *
 * Fires on mount with empty string (so the list renders immediately
 * without typing). 300ms debounce keeps keystrokes from spamming the RPC.
 *
 * Returns React Query state plus the debounced term (handy if the caller
 * wants to skip rendering while still typing).
 */
export function useAdminUserSearch(query: string) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  const trimmed = debouncedQuery.trim();

  const result = useQuery<AdminUserListRow[]>({
    queryKey: ['admin', 'user-list', trimmed],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_list_users', {
        p_q: trimmed.length > 0 ? trimmed : null,
        p_limit: ADMIN_SEARCH_LIMIT,
      });
      if (error) {
        captureError(error as Error, { feature: 'admin-user-search' });
        throw error;
      }
      return (data ?? []) as AdminUserListRow[];
    },
    // Fire on mount with empty string — empty query is now valid and
    // returns the recent-users list.
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    debouncedQuery: trimmed,
  };
}
